import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;

const letterHeadApi = {
  list: (vendorProfileId, search = "", signal) =>
    axiosInstance.get(
      `/api/superadmin/vendors/${vendorProfileId}/letter-heads/?q=${encodeURIComponent(search)}`,
      { signal }
    ),

  create: (vendorProfileId, formData) =>
    axiosInstance.post(`/api/superadmin/vendors/${vendorProfileId}/letter-heads/`, formData),

  patch: (vendorProfileId, letterHeadId, body) =>
    axiosInstance.patch(
      `/api/superadmin/vendors/${vendorProfileId}/letter-heads/${letterHeadId}/`,
      body
    ),

  delete: (vendorProfileId, letterHeadId) =>
    axiosInstance.delete(
      `/api/superadmin/vendors/${vendorProfileId}/letter-heads/${letterHeadId}/`
    ),
};

const isHtmlFile  = (item) => item?.content_type === "text/html"          || item?.file_name?.toLowerCase().endsWith(".html");
const isPdfFile   = (item) => item?.content_type === "application/pdf"    || item?.file_name?.toLowerCase().endsWith(".pdf");
const isImageFile = (item) => item?.content_type?.startsWith("image/")    || /\.(jpe?g|png|gif|webp|svg)$/i.test(item?.file_name || "");

const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const LoaderIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: "lh-spin 0.8s linear infinite", flexShrink: 0 }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const FileTextIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const StarIcon = ({ filled = false }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const AlertTriangleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const CodeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
  </svg>
);
const PdfIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="12" y2="17" />
    <path d="M9 11h1.5a1.5 1.5 0 0 1 0 3H9V11z" />
  </svg>
);
const XCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

let _lhToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _lhToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error:   (title, msg) => toast._push({ type: "error",   title, msg }),
  info:    (title, msg) => toast._push({ type: "info",    title, msg }),
  loading: (title)      => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)         => { if (id != null) _lhToastSetter?.((p) => p.filter((t) => t.id !== id)); },
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting]   = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10,
        borderLeft: `4px solid ${meta.bar}`,
        boxShadow: "0 4px 20px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.06)",
        minWidth: 300, maxWidth: 360,
        animation: exiting ? "lh-alOut .28s ease forwards" : "lh-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards",
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading"
            ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "lh-spin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
            : <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button
          aria-label="Dismiss notification"
          onClick={dismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width .1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _lhToastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const SkeletonBlock = ({ w, h = 14 }) => (
  <div style={{ height: h, width: w, borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "lh-shimmer 1.4s infinite" }} />
);

const SourceBadge = ({ source, display }) => {
  const cfg = {
    generated: { bg: "#ede9fe", color: "#6d28d9" },
    uploaded:  { bg: "#dbeafe", color: "#1d4ed8" },
  };
  const c = cfg[source] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color }}>
      {display || source}
    </span>
  );
};

const FieldError = ({ msg }) =>
  msg ? (
    <span role="alert" style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 }}>
      <AlertTriangleIcon />{msg}
    </span>
  ) : null;

const ConfirmDeleteModal = ({ title, onConfirm, onCancel, loading }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !loading) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, loading]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm deletion"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
      onClick={() => !loading && onCancel()}>
      <div
        style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 400, padding: 28, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "lh-fadeUp .2s ease" }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div>
            <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Delete Letter Head?</h3>
            <p style={{ margin: 0, fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
              "<strong>{title}</strong>" will be permanently removed. This action cannot be undone.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 8, width: "100%" }}>
            <button
              onClick={onCancel}
              disabled={loading}
              style={{ flex: 1, padding: "10px 0", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}>
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              aria-busy={loading}
              style={{ flex: 1, padding: "10px 0", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", background: loading ? "#fca5a5" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {loading ? <><LoaderIcon size={14} /> Deleting…</> : <><TrashIcon /> Delete</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LetterHeadFormModal = ({ mode, initialData, vendorProfileId, onClose, onSuccess }) => {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    title:      initialData?.title      ?? "",
    source:     initialData?.source     ?? "uploaded",
    is_default: initialData?.is_default ?? false,
  });
  const [file,     setFile]     = useState(null);
  const [preview,  setPreview]  = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const fileInputRef = useRef(null);
  const loadToastRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      toast.dismiss(loadToastRef.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ["image/jpeg", "image/png", "image/jpg", "text/html", "application/pdf"];
    if (!allowed.includes(f.type)) {
      toast.error("Invalid File Type", "Only JPG, PNG, HTML, or PDF files are allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File Too Large", `"${f.name}" exceeds the 10 MB limit.`);
      return;
    }
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
    setErrors((p) => ({ ...p, file: null }));
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())        e.title = "Title is required.";
    if (!isEdit && !file)          e.file  = "Please select a file to upload.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    loadToastRef.current = toast.loading(isEdit ? "Updating letter head…" : "Creating letter head…");

    try {
      let data;

      if (isEdit) {
        const { data: resData } = await letterHeadApi.patch(vendorProfileId, initialData.id, {
          title:      form.title.trim(),
          is_default: form.is_default,
        });
        data = resData;
      } else {
        const fd = new FormData();
        fd.append("title",      form.title.trim());
        fd.append("source",     form.source);
        fd.append("is_default", form.is_default ? "true" : "false");
        fd.append("file",       file);
        const { data: resData } = await letterHeadApi.create(vendorProfileId, fd);
        data = resData;
      }

      if (data?.status >= 400) {
        const fieldErrors = {};
        Object.entries(data?.data || {}).forEach(([k, v]) => {
          if (k !== "detail" && k !== "message") {
            fieldErrors[k] = Array.isArray(v) ? v[0] : String(v);
          }
        });
        if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
        const detail = data?.data?.detail || data?.data?.message || `Server error (HTTP ${data?.status})`;
        throw new Error(detail);
      }

      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.success(
        isEdit ? "Letter Head Updated" : "Letter Head Created",
        isEdit ? `"${form.title.trim()}" has been updated successfully.`
               : `"${form.title.trim()}" has been uploaded successfully.`
      );
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.error(
        isEdit ? "Update Failed" : "Upload Failed",
        err.message || "An unexpected error occurred. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Edit letter head" : "Upload letter head"}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div
        style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "lh-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: isEdit ? "linear-gradient(135deg,#f59e0b,#d97706)" : "linear-gradient(135deg,#8b5cf6,#6366f1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isEdit ? <EditIcon /> : <FileTextIcon />}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{isEdit ? "Edit Letter Head" : "Upload Letter Head"}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>{isEdit ? "Update title or default status" : "Upload a new letter head for this vendor"}</p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#64748b"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label htmlFor="lh-title" style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em" }}>
              Title <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              id="lh-title"
              value={form.title}
              onChange={(e) => {
                setForm((p) => ({ ...p, title: e.target.value }));
                if (errors.title) setErrors((p) => ({ ...p, title: null }));
              }}
              placeholder="e.g. Company Letter Head – Style 1"
              disabled={saving}
              style={{ padding: "9px 12px", border: `1px solid ${errors.title ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", transition: "border-color .15s" }}
              onFocus={(e) => { e.target.style.borderColor = errors.title ? "#f87171" : "#3b82f6"; }}
              onBlur={(e)  => { e.target.style.borderColor = errors.title ? "#fca5a5" : "#e2e8f0"; }}
            />
            <FieldError msg={errors.title} />
          </div>

          {!isEdit && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label htmlFor="lh-source" style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em" }}>Source</label>
              <div style={{ position: "relative" }}>
                <select
                  id="lh-source"
                  value={form.source}
                  disabled={saving}
                  onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
                  style={{ width: "100%", padding: "9px 32px 9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", appearance: "none", cursor: "pointer" }}>
                  <option value="uploaded">Uploaded</option>
                  <option value="generated">Generated</option>
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
              </div>
            </div>
          )}

          {!isEdit && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em" }}>
                File <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <div
                role="button"
                aria-label="File upload area – click or drag and drop"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? "#8b5cf6" : file ? "#22c55e" : errors.file ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 12, padding: "24px 20px", textAlign: "center", cursor: saving ? "not-allowed" : "pointer", background: dragOver ? "#f5f3ff" : file ? "#f0fdf4" : "#f8fafc", transition: "all .2s" }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,text/html,application/pdf"
                  style={{ display: "none" }}
                  disabled={saving}
                  onChange={(e) => handleFile(e.target.files[0])}
                />
                {preview ? (
                  <img src={preview} alt="Preview" style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }} />
                ) : (
                  <>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: file ? "#dcfce7" : "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                      {file ? <CheckIcon /> : <UploadIcon />}
                    </div>
                    <p style={{ margin: "0 0 4px", fontSize: 13.5, fontWeight: 600, color: "#374151" }}>
                      {file ? file.name : "Click or drag & drop"}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                      {file ? `${(file.size / 1024).toFixed(0)} KB` : "Supported: JPG, PNG, HTML, PDF · Max 10 MB"}
                    </p>
                  </>
                )}
              </div>
              <FieldError msg={errors.file} />
              {file && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                  <FileTextIcon />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "#166534", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                  <button
                    aria-label="Remove selected file"
                    onClick={(e) => { e.stopPropagation(); clearFile(); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 2 }}>
                    <CloseIcon />
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: 13, fontWeight: 550, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
              <StarIcon filled={form.is_default} /> Set as Default Letter Head
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={form.is_default}
              aria-label="Set as default letter head"
              onClick={() => setForm((p) => ({ ...p, is_default: !p.is_default }))}
              style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: form.is_default ? "#16a34a" : "#e2e8f0", position: "relative", transition: "background .2s", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 2, left: form.is_default ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)", transition: "left .2s" }} />
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            aria-busy={saving}
            style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: saving ? "#c4b5fd" : (isEdit ? "#f59e0b" : "#8b5cf6"), color: "#fff", display: "flex", alignItems: "center", gap: 7 }}>
            {saving ? <><LoaderIcon size={14} /> Saving…</> : isEdit ? <><EditIcon /> Update</> : <><UploadIcon /> Upload</>}
          </button>
        </div>
      </div>
    </div>
  );
};


const CardThumbnail = ({ item, onPreview }) => {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const html = isHtmlFile(item);
  const pdf  = isPdfFile(item);
  const img  = isImageFile(item) && !html && !pdf;

  const renderContent = () => {
    if (html) return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#ddd6fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CodeIcon />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#7c3aed" }}>HTML Template</span>
      </div>
    );

    if (pdf) return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <PdfIcon />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#dc2626" }}>PDF Document</span>
      </div>
    );

    if (img && !imgError) return (
      <img
        src={item.file_url}
        alt={item.title}
        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .25s", transform: hovered ? "scale(1.04)" : "scale(1)" }}
        onError={() => setImgError(true)}
      />
    );

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: "#94a3b8" }}>
        <FileTextIcon />
        <span style={{ fontSize: 11.5 }}>Preview unavailable</span>
      </div>
    );
  };

  return (
    <div
      style={{ height: 130, background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", overflow: "hidden" }}
      onClick={() => onPreview(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {renderContent()}

      <div style={{
        position: "absolute", inset: 0,
        background: "rgba(15,23,42,.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        opacity: hovered ? 1 : 0,
        transition: "opacity .2s",
        pointerEvents: "none",
      }}>
        <span style={{ background: "rgba(255,255,255,.9)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 5 }}>
          <EyeIcon /> Preview
        </span>
      </div>
    </div>
  );
};

const LetterHeadCard = ({ item, vendorProfileId, onDeleted, onUpdated, onPreview }) => {
  const [showDelete, setShowDelete] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [settingDef, setSettingDef] = useState(false);
  const loadToastRef = useRef(null);

  useEffect(() => {
    return () => { toast.dismiss(loadToastRef.current); };
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    loadToastRef.current = toast.loading("Deleting letter head…");
    try {
      const { data } = await letterHeadApi.delete(vendorProfileId, item.id);
      if (data?.status >= 400 && data?.status !== 404) {
        throw new Error(data?.data?.detail || data?.data?.message || "Delete failed");
      }
      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.success("Deleted", `"${item.title}" has been removed.`);
      onDeleted(item.id);
    } catch (err) {
      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.error("Delete Failed", err.message || "Could not delete this letter head. Please try again.");
      setShowDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async () => {
    if (item.is_default || settingDef) return;
    setSettingDef(true);
    loadToastRef.current = toast.loading("Setting as default…");
    try {
      const { data } = await letterHeadApi.patch(vendorProfileId, item.id, { is_default: true });
      if (data?.status >= 400) {
        throw new Error(data?.data?.detail || data?.data?.message || "Update failed");
      }
      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.success("Default Updated", `"${item.title}" is now the default letter head.`);
      onUpdated(data?.data ?? data);
    } catch (err) {
      toast.dismiss(loadToastRef.current);
      loadToastRef.current = null;
      toast.error("Failed to Set Default", err.message || "Could not update the default. Please try again.");
    } finally {
      setSettingDef(false);
    }
  };

  return (
    <>
      <div style={{ background: "#fff", border: `1px solid ${item.is_default ? "#c4b5fd" : "#e2e8f0"}`, borderRadius: 12, overflow: "hidden", boxShadow: item.is_default ? "0 0 0 2px #ede9fe" : "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s", position: "relative" }}>

        {item.is_default && (
          <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2, display: "flex", alignItems: "center", gap: 4, padding: "3px 9px", background: "#8b5cf6", borderRadius: 20, fontSize: 11.5, fontWeight: 700, color: "#fff" }}>
            <StarIcon filled={true} /> Default
          </div>
        )}

        <CardThumbnail item={item} onPreview={onPreview} />

        <div style={{ padding: "12px 14px" }}>
          <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.title}>
            {item.title}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            <SourceBadge source={item.source} display={item.source_display} />
            <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{(item.size / 1024).toFixed(1)} KB</span>
          </div>

          <div style={{ fontSize: 11.5, color: "#94a3b8", display: "flex", flexDirection: "column", gap: 3, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <CalendarIcon /><span>Uploaded: {item.uploaded_at}</span>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <CalendarIcon /><span>Updated: {item.updated_at}</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button
              onClick={handleSetDefault}
              disabled={item.is_default || settingDef}
              aria-label={item.is_default ? "Already the default" : "Set as default"}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", background: item.is_default ? "#f5f3ff" : "#ede9fe", color: item.is_default ? "#7c3aed" : "#5b21b6", border: "1px solid #ddd6fe", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: item.is_default || settingDef ? "not-allowed" : "pointer", opacity: item.is_default ? 0.6 : 1 }}>
              {settingDef ? <LoaderIcon size={12} /> : <StarIcon filled={item.is_default} />}
              {item.is_default ? "Default" : "Set Default"}
            </button>

            <a
              href={item.file_url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${item.title} in new tab`}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}>
              <LinkIcon /> Open
            </a>

            <button
              onClick={() => setShowEdit(true)}
              aria-label={`Edit ${item.title}`}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#d97706", cursor: "pointer" }}>
              <EditIcon /> Edit
            </button>

            <button
              onClick={() => setShowDelete(true)}
              aria-label={`Delete ${item.title}`}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#dc2626", cursor: "pointer" }}>
              <TrashIcon /> Delete
            </button>
          </div>
        </div>
      </div>

      {showEdit && (
        <LetterHeadFormModal
          mode="edit"
          initialData={item}
          vendorProfileId={vendorProfileId}
          onClose={() => setShowEdit(false)}
          onSuccess={(updated) => onUpdated(updated)}
        />
      )}

      {showDelete && (
        <ConfirmDeleteModal
          title={item.title}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => !deleting && setShowDelete(false)}
        />
      )}
    </>
  );
};

const LetterHeadSkeleton = () => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
    {[1, 2, 3, 4].map((i) => (
      <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ height: 130, background: "#f8fafc" }} />
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
          <SkeletonBlock w="80%" h={14} />
          <SkeletonBlock w="50%" h={12} />
          <SkeletonBlock w="60%" h={11} />
          <div style={{ display: "flex", gap: 6 }}>
            <SkeletonBlock w={80} h={28} />
            <SkeletonBlock w={60} h={28} />
          </div>
        </div>
      </div>
    ))}
  </div>
);


const PreviewModal = ({ item, onClose }) => {
  const html = isHtmlFile(item);
  const pdf  = isPdfFile(item);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const renderPreview = () => {
    if (html || pdf) {
      return (
        <iframe
          src={item.file_url}
          title={item.title}
          style={{ width: "100%", height: "78vh", border: "none", display: "block" }}
        />
      );
    }
    return (
      <img
        src={item.file_url}
        alt={item.title}
        style={{ maxWidth: "90vw", maxHeight: "78vh", objectFit: "contain", display: "block", margin: "0 auto" }}
        onError={(e) => {
          e.target.style.display = "none";
          e.target.insertAdjacentHTML("afterend", `<div style="padding:40px;text-align:center;color:#94a3b8;font-size:14px;">Image could not be loaded.</div>`);
        }}
      />
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${item.title}`}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 20 }}
      onClick={onClose}>
      <div style={{ position: "relative", maxWidth: "92vw", maxHeight: "92vh", width: "100%" }} onClick={(e) => e.stopPropagation()}>

        <button
          aria-label="Close preview"
          onClick={onClose}
          style={{ position: "absolute", top: -14, right: -14, width: 34, height: 34, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.3)", zIndex: 1 }}>
          <CloseIcon />
        </button>

        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 16px", background: "rgba(255,255,255,.1)", borderRadius: 20, fontSize: 13, fontWeight: 600, color: "#fff" }}>
            {item.title}
            {item.is_default && <span style={{ fontSize: 11, background: "#8b5cf6", padding: "2px 7px", borderRadius: 10 }}>Default</span>}
            {pdf  && <span style={{ fontSize: 11, background: "#dc2626", padding: "2px 7px", borderRadius: 10 }}>PDF</span>}
            {html && <span style={{ fontSize: 11, background: "#7c3aed", padding: "2px 7px", borderRadius: 10 }}>HTML</span>}
          </span>
        </div>

        <div style={{ borderRadius: 12, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,.5)", maxHeight: "78vh", background: "#fff" }}>
          {renderPreview()}
        </div>

        <div style={{ textAlign: "center", marginTop: 12 }}>
          <a
            href={item.file_url}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", background: "#fff", borderRadius: 8, color: "#1e293b", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
            <LinkIcon /> Open in New Tab
          </a>
        </div>
      </div>
    </div>
  );
};

const LetterHead = ({ vendorProfileId }) => {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [search,      setSearch]      = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [previewItem, setPreviewItem] = useState(null);

  const debounceRef  = useRef(null);
  const abortRef     = useRef(null);

  const fetchList = useCallback(async (q = "") => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const { data } = await letterHeadApi.list(vendorProfileId, q, controller.signal);

      if (data?.status === 401) { toast.error("Session Expired", "Your session has expired. Please log in again."); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view letter heads."); return; }
      if (data?.status === 404) {
        const msg = data?.data?.detail?.toLowerCase().includes("no vendorprofile")
          ? "No vendor profile found for the given ID."
          : data?.data?.detail || "The requested resource was not found.";
        toast.error("Not Found", msg);
        setItems([]);
        return;
      }
      if (data?.status >= 400) throw new Error("Load failed");

      const list = data?.results ?? data?.data ?? (Array.isArray(data) ? data : []);
      setItems(list);
    } catch (err) {
      if (err.name === "AbortError") return;
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Failed to Load",
        err?.name === "TypeError"
          ? "Unable to reach the server. Check your connection and try again."
          : err.message || "Unable to fetch letter heads. Please try again."
      );
      setItems([]);
    } finally {
     
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [vendorProfileId]);

  useEffect(() => {
    fetchList(search);
    return () => {
      abortRef.current?.abort();
      clearTimeout(debounceRef.current);
    };
  }, [fetchList, search]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 350);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
  };

  const handleCreated = (newItem) => {
    setItems((prev) => {
      let next = newItem.is_default
        ? prev.map((i) => ({ ...i, is_default: false }))
        : [...prev];
      return [newItem, ...next];
    });
  };

  const handleUpdated = (updated) => {
    setItems((prev) => {
      let next = prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i));
      if (updated.is_default) next = next.map((i) => (i.id === updated.id ? i : { ...i, is_default: false }));
      return next;
    });
  };

  const handleDeleted = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const defaultItem = items.find((i) => i.is_default);

  const stats = [
    {
      label: "Total",
      value: <span style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>{items.length}</span>,
      bg: "#f8fafc", border: "#e2e8f0",
    },
    {
      label: "Default",
      value: defaultItem
        ? <span style={{ fontSize: 12.5, fontWeight: 700, color: "#7c3aed", background: "#ede9fe", padding: "2px 8px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <StarIcon filled={true} /> {defaultItem.title.length > 28 ? defaultItem.title.slice(0, 28) + "…" : defaultItem.title}
          </span>
        : <span style={{ fontSize: 12, color: "#94a3b8" }}>None set</span>,
      bg: "#faf5ff", border: "#e9d5ff",
    },
    {
      label: "Generated",
      value: <span style={{ fontSize: 16, fontWeight: 800, color: "#6d28d9" }}>{items.filter((i) => i.source === "generated").length}</span>,
      bg: "#faf5ff", border: "#e9d5ff",
    },
    {
      label: "Uploaded",
      value: <span style={{ fontSize: 16, fontWeight: 800, color: "#1d4ed8" }}>{items.filter((i) => i.source === "uploaded").length}</span>,
      bg: "#eff6ff", border: "#bfdbfe",
    },
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", color: "#1e293b" }}>
      <style>{`
        @keyframes lh-shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes lh-fadeUp   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes lh-alIn     { from{opacity:0;transform:translateX(24px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes lh-alOut    { from{opacity:1} to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes lh-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        * { box-sizing:border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6366f1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Letter Heads</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>Manage vendor letter head templates</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
              <SearchIcon />
            </span>
            <input
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search letter heads…"
              aria-label="Search letter heads"
              style={{ padding: "8px 12px 8px 32px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", width: 210 }}
              onFocus={(e) => e.target.style.borderColor = "#8b5cf6"}
              onBlur={(e)  => e.target.style.borderColor = "#e2e8f0"}
            />
            {searchInput && (
              <button
                aria-label="Clear search"
                onClick={clearSearch}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 2 }}>
                <XCircleIcon />
              </button>
            )}
          </div>

          <button
            onClick={() => fetchList(search)}
            disabled={loading}
            aria-label="Refresh list"
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#f1f5f9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
            {loading ? <LoaderIcon size={15} /> : <RefreshIcon />} Refresh
          </button>

          <button
            onClick={() => setShowForm(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#8b5cf6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = ".85"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}>
            <PlusIcon /> Upload Letter Head
          </button>
        </div>
      </div>

      {!loading && items.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          {stats.map(({ label, value, bg, border }) => (
            <div key={label} style={{ padding: "10px 16px", background: bg, border: `1px solid ${border}`, borderRadius: 10, display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</span>
              {value}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <LetterHeadSkeleton />
      ) : items.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 12, textAlign: "center" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#374151" }}>
              {searchInput ? "No Results Found" : "No Letter Heads Yet"}
            </p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#94a3b8" }}>
              {searchInput
                ? `No letter heads matched "${searchInput}". Try a different term or clear the search.`
                : "This vendor doesn't have any letter heads yet. Upload one to get started."}
            </p>
          </div>
          {searchInput ? (
            <button
              onClick={clearSearch}
              style={{ marginTop: 4, padding: "9px 20px", background: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              Clear Search
            </button>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", background: "#8b5cf6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              <PlusIcon /> Upload Letter Head
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16, animation: "lh-fadeUp .2s ease" }}>
          {items.map((item) => (
            <LetterHeadCard
              key={item.id}
              item={item}
              vendorProfileId={vendorProfileId}
              onDeleted={handleDeleted}
              onUpdated={handleUpdated}
              onPreview={setPreviewItem}
            />
          ))}
        </div>
      )}

      {showForm && (
        <LetterHeadFormModal
          mode="create"
          initialData={null}
          vendorProfileId={vendorProfileId}
          onClose={() => setShowForm(false)}
          onSuccess={handleCreated}
        />
      )}

      {previewItem && (
        <PreviewModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </div>
  );
};

export default LetterHead;